import React, {useEffect, useMemo, useCallback} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useForm, Controller, SubmitHandler} from 'react-hook-form';
import ArrModal, {ModalButton} from '../../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrTextArea from '../../../../common/arr-text-area/ArrTextArea';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../shared/types';
import {
  MyTranscriptListMenuKey,
  MyTranscriptCreateKey,
  TranscriptFormValues,
  TranscriptItemDataType,
} from '../../AiInvestigateUtils';
import './TranscriptActionsModal.scss';
export type TranscriptActionMode =
  | MyTranscriptCreateKey.Create
  | MyTranscriptListMenuKey.Remove
  | MyTranscriptListMenuKey.Edit;

interface Props {
  open: boolean;
  onClose: () => void;
  selectedTranscript?: TranscriptItemDataType | null;
  mode: TranscriptActionMode;
  onConfirm: (data?: TranscriptFormValues) => void;
  indexData?: ISelectItem[];
}

const MAX_LENGTH = 35;
const TRANSCRIPT_DESCRIPTION_LENGTH = 200;

const TranscriptActionModal: React.FC<Props> = ({
  open,
  onClose,
  selectedTranscript,
  mode,
  onConfirm,
  indexData = [],
}) => {
  const isRemoved = mode === MyTranscriptListMenuKey.Remove;
  const isEdit = mode === MyTranscriptListMenuKey.Edit;
  const isCreate = mode === MyTranscriptCreateKey.Create;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {errors, isValid},
  } = useForm<TranscriptFormValues>({
    defaultValues: {
      name: selectedTranscript?.transcriptName ?? '',
      description: selectedTranscript?.description ?? '',
      index: null,
    },
    mode: 'onChange',
  });

  const values = watch();

  const hasValidName =
    Boolean(values.name?.trim()) && values.name.length <= MAX_LENGTH;
  const hasValidDescription =
    Boolean(values.description?.trim()) &&
    values.description.length <= TRANSCRIPT_DESCRIPTION_LENGTH;
  const hasValidIndex = !isCreate || values.index != null;
  const isNameOrDescriptionEmpty = !hasValidName || !hasValidDescription;
  const hasEditChanges =
    values.name.trim() !== (selectedTranscript?.transcriptName ?? '').trim() ||
    values.description.trim() !==
      (selectedTranscript?.description ?? '').trim();

  useEffect(() => {
    if (open) {
      reset({
        name: selectedTranscript?.transcriptName ?? '',
        description: selectedTranscript?.description ?? '',
        index: null,
      });
    }
  }, [open, selectedTranscript, reset]);

  const onSubmit: SubmitHandler<TranscriptFormValues> = useCallback(
    (data) => {
      onConfirm({
        ...data,
        name: data.name.trim(),
        description: data.description.trim(),
      });
    },
    [onConfirm]
  );

  const isConfirmDisabled =
    !isRemoved &&
    (isNameOrDescriptionEmpty ||
      (isCreate ? !hasValidIndex || !isValid : !hasEditChanges));

  const labels = {
    default: {
      confirmButtonLabel: t(
        'ai.investigate.listItem.deleteModalContent.removeBtn'
      ),
      headerTitle: t('ai.investigate.listItem.deleteModalContent.confirmText'),
      modalTitle: t('ai.investigate.listItem.deleteModalContent.title'),
    },
    create: {
      confirmButtonLabel: t('ai.investigate.transcripts.modal.createBtn'),
      headerTitle: t('ai.investigate.transcripts.modal.header'),
      modalTitle: t('ai.investigate.transcripts.modal.header'),
    },
    edit: {
      confirmButtonLabel: t('common.save'),
      headerTitle: undefined,
      modalTitle: t('ai.investigate.listItem.editModalContent.title'),
    },
  } as const;

  const type = isCreate ? 'create' : isEdit ? 'edit' : 'default';
  const {confirmButtonLabel, headerTitle, modalTitle} = labels[type];

  const dialogButtons: ModalButton[] = useMemo(
    () => [
      {
        id: 'cancel',
        label: t('common.cancel'),
        variant: 'secondary',
        dataTestId: 'cancel-button',
        onClick: onClose,
        ariaLabel: t('common.cancel'),
      },
      {
        id: 'confirm',
        label: confirmButtonLabel,
        variant: 'primary',
        type: isRemoved ? 'button' : 'submit',
        dataTestId: 'confirm-button',
        onClick: isRemoved ? () => onConfirm() : handleSubmit(onSubmit),
        disabled: isConfirmDisabled,
        ariaLabel: t(
          isRemoved
            ? 'ai.investigate.listItem.deleteModalContent.removeName'
            : isCreate
              ? 'ai.investigate.transcripts.modal.createBtn'
              : 'common.save'
        ),
      },
    ],
    [
      isCreate,
      isEdit,
      isRemoved,
      confirmButtonLabel,
      isConfirmDisabled,
      handleSubmit,
      onSubmit,
      onClose,
      onConfirm,
    ]
  );

  return (
    <ArrModal
      showModal={open}
      onClose={onClose}
      headerTitle={headerTitle}
      title={modalTitle}
      description={t(
        isCreate || isEdit
          ? ''
          : 'ai.investigate.listItem.deleteModalContent.description',
        {transcriptName: values.name}
      )}
      buttons={dialogButtons}
      className="actionModal"
      dataTestId="transcript-actions-modal"
      headerTitleClassName="label2_size header-title"
      autoFocusCloseButton
    >
      {!isRemoved && (
        <Box className="actionModal-container">
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="transcript-actions-modal-form"
          >
            <Controller
              name="name"
              control={control}
              rules={{
                required: t(''),
              }}
              render={({field}) => (
                <ArrTextField
                  {...field}
                  label={t('ai.investigate.transcripts.modal.label')}
                  dataTestId="transcripts-modal-label"
                  fullWidth
                  value={field.value || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    field.onChange(event.target.value.slice(0, MAX_LENGTH))
                  }
                  supportText={errors.name?.message}
                  showErrorIcon={!!errors.name}
                  data-testid="transcript-input"
                  aria-invalid={!!errors.name}
                />
              )}
            />

            <Typography
              className={`typo-text body4_size character-count ${values.name.length >= MAX_LENGTH ? 'character-count--error ' : ''}`}
              data-testid="transcript-name-char-count"
            >
              {values.name.length}/{MAX_LENGTH}
            </Typography>

            <Box mt={2}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: t(''),
                }}
                render={({field}) => (
                  <ArrTextArea
                    dataTestId={`field-${field.name}`}
                    {...field}
                    className="transcript-description-field"
                    data-testid="description-input"
                    label={t('ai.investigate.transcripts.modal.description')}
                    maxRows={3}
                    value={field.value || ''}
                    rows={2}
                    maxLength={TRANSCRIPT_DESCRIPTION_LENGTH}
                    placeholder={t(
                      'ai.investigate.transcripts.modal.descriptionPlaceholder'
                    )}
                  />
                )}
              />
              <Typography
                className={`typo-text body4_size character-count ${values.description.length >= TRANSCRIPT_DESCRIPTION_LENGTH ? 'character-count--error' : ''}`}
                data-testid="transcript-description-char-count"
              >
                {values.description.length}/{TRANSCRIPT_DESCRIPTION_LENGTH}
              </Typography>
            </Box>

            {isCreate && (
              <Box mt={2}>
                <Controller
                  name="index"
                  control={control}
                  rules={{
                    required: t('common.required'),
                  }}
                  render={({field}) => (
                    <ArrSingleSelectDropdown
                      dropdownLabel={t(
                        'ai.investigate.transcripts.modal.dropDownHeader'
                      )}
                      dropdownItems={indexData}
                      selectedValues={field.value}
                      onSelectionChange={field.onChange}
                      sizeVariant="medium"
                      dataTestId="asd-index-input"
                      searchable
                      showNoResultsText
                      disabled={indexData.length === 0}
                    />
                  )}
                />
              </Box>
            )}
          </form>
        </Box>
      )}
    </ArrModal>
  );
};

export default TranscriptActionModal;
